import { useEffect, useMemo, useState } from 'react';
import { Select, Spin, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { matchesSearch } from '@/utils/normalize';
import { formatAmisEmployee } from '@/utils/amisEmployee';
import type { PosStaff, PosStaffSalesChannel } from '../types';
import {
  POS_STAFF_PAGE_LIMIT,
  posStaffQueryKey,
  posStaffScopeOf,
} from '../utils/posStaffQuery';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  value?: string;
  onChange: (staffId: string) => void;
  /** Called alongside onChange with the selected staff's display name. */
  onChangeWithLabel?: (staffId: string, name: string) => void;
  storeId?: string;
  allowedStaffIds?: string[];
  /**
   * TCR-2429 — phạm vi danh sách lấy từ BE. `'b2b'` = NVKD GT/MT toàn công ty (người được ghi
   * nhận doanh số trên đơn B2B); bỏ trống = NV thuộc chi nhánh như trước. Phải nằm trong
   * `queryKey` thì React Query mới coi hai phạm vi là hai bộ dữ liệu khác nhau.
   */
  salesChannel?: PosStaffSalesChannel;
  placeholder?: string;
  warning?: boolean;
  error?: boolean;
  errorText?: string;
  initialLabel?: string;
  disabled?: boolean;
}

/** Dòng phụ dưới tên: "NV0003 · 0388517076" — bỏ phần rỗng, không để lại dấu · lửng. */
function buildStaffMeta(staff: PosStaff): string {
  return [staff.staff_code, staff.phone].filter((part) => part && part.trim() !== '').join(' · ');
}

/** TCR-2492 — các dòng "AMIS: mã · tên" của nhân viên (mỗi mapping một dòng). */
function buildAmisLines(staff: PosStaff): string[] {
  return (staff.amis_employees ?? []).map((e) => `AMIS: ${formatAmisEmployee(e)}`);
}

/**
 * TCR-1576: khớp theo tên KHÔNG DẤU, mã NV, hoặc SĐT. Lọc tại chỗ trên trang đã
 * tải để kết quả hẹp lại ngay khi gõ, trong khi lượt gọi BE `?search=` (debounce)
 * đang bay — nếu chỉ dựa vào BE thì danh sách đứng im mất một nhịp.
 */
function matchesStaff(staff: PosStaff, keyword: string): boolean {
  const trimmed = keyword.trim();
  if (!trimmed) return true;
  // TCR-2492: BE `search` khớp cả mã/tên AMIS, nên lọc tại chỗ cũng phải khớp, nếu không kết quả BE bị lọc mất.
  const amisFields = (staff.amis_employees ?? []).flatMap((e) => [e.code, e.name]);
  return [staff.name, staff.staff_code, staff.phone, ...amisFields].some(
    (field) => !!field && matchesSearch(trimmed, field),
  );
}

export function StaffAsyncSelect({
  value,
  onChange,
  onChangeWithLabel,
  storeId,
  allowedStaffIds,
  salesChannel,
  placeholder = 'Chọn nhân viên',
  warning = false,
  error = false,
  errorText,
  initialLabel,
  disabled = false,
}: Props) {
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(keyword.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]);

  const { data: allStaff = [], isLoading } = useQuery({
    // `salesChannel` nằm trong key: cùng chi nhánh nhưng phạm vi B2B (NVKD toàn công ty) là
    // một bộ dữ liệu khác, để chung key thì cache của đơn B2C trả về danh sách NV của store.
    queryKey: posStaffQueryKey({ storeId, salesChannel, keyword: debouncedKeyword }),
    // `search` phủ được chi nhánh có nhiều hơn POS_STAFF_PAGE_LIMIT nhân viên: BE
    // (buildStaffSearchFilter) khớp mã NV / email / SĐT / tên không dấu / viết tắt.
    queryFn: () =>
      posService.getStaffList({
        limit: POS_STAFF_PAGE_LIMIT,
        ...(debouncedKeyword ? { search: debouncedKeyword } : {}),
        // TCR-2429: chỉ gửi khi là đơn B2B — đơn B2C giữ nguyên request cũ (không có tham số).
        ...(salesChannel ? { salesChannel } : {}),
      }),
    staleTime: 2 * 60 * 1000,
    /**
     * TCR-2429 (Codex finding 1) — giữ danh sách cũ CHỈ khi vẫn cùng phạm vi (cùng store, cùng
     * `salesChannel`). Trước đây `(prev) => prev` giữ dữ liệu của query key TRƯỚC ĐÓ bất kể phạm
     * vi, nên lúc chuyển đơn B2C → B2B (hoặc ngược lại) ô chọn vẫn bày danh sách NV của chi nhánh
     * trong khi danh sách NVKD đang tải — bấm vào là gán/gửi một người không thuộc danh sách NVKD.
     * Khác phạm vi ⇒ trả `undefined`: ô trống + loading, không có gì sai để bấm. Cùng phạm vi (chỉ
     * đổi từ khoá tìm) ⇒ vẫn giữ như cũ để danh sách không nháy khi đang gõ.
     */
    placeholderData: (prev, prevQuery) => {
      const prevScope = posStaffScopeOf(prevQuery?.queryKey);
      const sameScope =
        !!prevScope && prevScope.storeId === storeId && prevScope.salesChannel === salesChannel;
      return sameScope ? prev : undefined;
    },
  });

  const options: PosStaff[] = useMemo(() => {
    const scoped =
      allowedStaffIds && allowedStaffIds.length > 0
        ? allStaff.filter((s) => allowedStaffIds.includes(s.staff_id))
        : [...allStaff];

    const filtered = scoped.filter((s) => matchesStaff(s, keyword));

    // Nhân viên đang được chọn phải luôn có mặt, kể cả khi họ không nằm trong
    // trang hiện tại — nếu không, ô sẽ hiện trống trên đơn cũ. Chỉ giữ khi
    // người dùng chưa gõ tìm, để lượt tìm không bị lẫn một dòng lạc đề.
    if (value && initialLabel && !keyword.trim() && !filtered.find((s) => s.staff_id === value)) {
      filtered.push({ staff_id: value, name: initialLabel } as PosStaff);
    }
    return filtered;
  }, [allStaff, allowedStaffIds, keyword, value, initialLabel]);

  const noAllowedStaff = allowedStaffIds !== undefined && allowedStaffIds.length === 0;
  // Error (required-but-empty) takes precedence over warning (no warehouse staff).
  const status: 'error' | 'warning' | undefined = error
    ? 'error'
    : warning || noAllowedStaff
      ? 'warning'
      : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>
        <Select
          style={{ flex: 1, minWidth: 0 }}
          placeholder={placeholder}
          value={value || undefined}
          onChange={(v, option) => {
            onChange(v);
            if (onChangeWithLabel && option && !Array.isArray(option)) {
              onChangeWithLabel(v, (option as { label?: string }).label ?? '');
            }
          }}
          showSearch
          // Lọc do component tự làm (theo tên không dấu / mã NV / SĐT) + BE search,
          // nên tắt bộ lọc mặc định của antd (chỉ so khớp label nguyên văn).
          filterOption={false}
          searchValue={keyword}
          onSearch={setKeyword}
          onOpenChange={(open) => {
            if (!open) setKeyword('');
          }}
          loading={isLoading}
          notFoundContent={isLoading ? <Spin size="small" /> : 'Không có nhân viên'}
          options={options.map((s) => ({
            value: s.staff_id,
            label: s.name,
            meta: buildStaffMeta(s),
            amisLines: buildAmisLines(s),
          }))}
          optionRender={(option) => (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span style={{ fontWeight: 500 }}>{option.data.label}</span>
              {option.data.meta ? (
                <span style={{ fontSize: 11, color: '#888' }}>{option.data.meta}</span>
              ) : null}
              {(option.data.amisLines as string[]).map((line) => (
                <span key={line} style={{ fontSize: 11, color: '#888' }}>{line}</span>
              ))}
            </div>
          )}
          allowClear={false}
          status={status}
          disabled={disabled}
        />
        {!error && (warning || noAllowedStaff) && (
          <Tooltip title="Không có nhân viên kho phụ trách">
            <WarningOutlined style={{ color: '#faad14', fontSize: 12 }} />
          </Tooltip>
        )}
      </div>
      {error && errorText && (
        <span style={{ color: '#ff4d4f', fontSize: 11, lineHeight: 1.2 }}>{errorText}</span>
      )}
    </div>
  );
}
